import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '../../components/shared/Button';
import Combobox from '../../components/shared/Combobox';
import ManualSlotSelectionModal from '../../components/planning/ManualSlotSelectionModal';
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';
import { calculateLoadTime, calculateFullnessDetails, getSuggestions } from '../../services/business/planningService';
import SegmentedProgressBar from '../../components/shared/SegmentedProgressBar';
import { useDataScope } from '../../hooks/useDataScope';
import { getDailyReservations, createReservation, updateReservation } from '../../services/business/reservationService';
import { PERMISSIONS } from '../../constants/permissions';
import { getDocks } from '../../services/business/dockService';
import { getAllTransporters } from '../../services/firestore/transporterService';
import { getVehicleTypes } from '../../services/business/vehicleTypeService';
import { getTransportModes } from '../../services/firestore/transportModeService';
import { getAllFactories } from '../../services/firestore/factoryService';
import { getAllDoorGroups } from '../../services/firestore/doorGroupService';
import { getGeneralSettings, getLoadingRules, getDockTypes } from '../../services/firestore/systemConfigService';
import { getNextSequenceNumber, createNumberRangeConfig } from '../../services/firestore/sequenceService';
import '../../styles/nav-tree.css';
import { Timestamp } from 'firebase/firestore';


const VehiclePlanning = ({ userData }) => {
    const { success, error: toastError } = useToast();
    const { user, can } = usePermissions(userData);
    const scope = useDataScope(userData);
    const tenantId = user?.tenantId;
    const location = useLocation();
    // Draft Logic
    const loadDraft = () => {
        try {
            const saved = localStorage.getItem('vehiclePlanningDraft');
            return saved ? JSON.parse(saved) : null;
        } catch (e) { return null; }
    };
    const draft = loadDraft();

    const [editingReservationId, setEditingReservationId] = useState(draft?.editingReservationId || null);

    // Data State
    const [transporters, setTransporters] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [docks, setDocks] = useState([]);
    const [factories, setFactories] = useState([]);
    const [doorGroups, setDoorGroups] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [transportModes, setTransportModes] = useState([]);
    const [dockTypes, setDockTypes] = useState([]);
    const [loadingRules, setLoadingRules] = useState([]);
    const [activeTab, setActiveTab] = useState('smart'); // 'smart' or 'manual'
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [manualSelection, setManualSelection] = useState(() => {
        const saved = localStorage.getItem('planningManualSelection');
        return saved ? JSON.parse(saved) : {
            factoryId: '',
            doorGroupId: '',
            dockId: '',
            selectedSlotTime: null
        };
    });

    useEffect(() => {
        // Persist only factory and door group, maybe dockId
        const toSave = {
            factoryId: manualSelection.factoryId,
            doorGroupId: manualSelection.doorGroupId,
            dockId: manualSelection.dockId,
            selectedSlotTime: null // Don't persist time as it's date specific
        };
        localStorage.setItem('planningManualSelection', JSON.stringify(toSave));
    }, [manualSelection.factoryId, manualSelection.doorGroupId, manualSelection.dockId]);

    // Form State
    const initialFormData = {
        transporterName: '',
        transporterId: null,
        transporterShortName: '', // New field
        plateNumber: '', // Optional now
        transportType: '', // Dynamic now
        vehicleType: '',
        vehicleTypeName: '',
        volume: '',
        weight: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        shareNotes: false,
        driverName: '',
        driverPhone: ''
    };

    const [formData, setFormData] = useState(initialFormData);

    // Calculated State
    const [metrics, setMetrics] = useState({
        loadTime: 30,
        fullness: 0,
        volumePct: 0,
        weightPct: 0,
        reservationDuration: 30
    });

    const [suggestions, setSuggestions] = useState([]);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [systemConfig, setSystemConfig] = useState(null); // { slotDuration: 30, ... }
    const [reservedDocNumber, setReservedDocNumber] = useState(draft?.reservedDocNumber || '');

    // Persistence Effect
    useEffect(() => {
        const stateToSave = {
            formData,
            reservedDocNumber,
            editingReservationId
        };
        localStorage.setItem('vehiclePlanningDraft', JSON.stringify(stateToSave));
    }, [formData, reservedDocNumber, editingReservationId]);

    // Initial Load
    useEffect(() => {
        if (tenantId) {
            loadInitialData();
        }
    }, [tenantId]);

    const fetchingRef = useRef(false);

    // Initial Load - Sequence Number
    useEffect(() => {
        // Prevent fetching if we are in EDIT mode (incoming from location state)
        if (location.state?.editReservation) return;

        if (tenantId && !reservedDocNumber && !fetchingRef.current && !editingReservationId) {
            fetchingRef.current = true;
            getNextSequenceNumber(tenantId, 'RESERVATION').then(async (num) => {
                if (!num) {
                    await createNumberRangeConfig({
                        tenantId,
                        entityName: 'RESERVATION',
                        prefix: 'REZ-',
                        startValue: 100000,
                        endValue: 999999,
                        description: 'Rezervasyon Numaraları'
                    });
                    return getNextSequenceNumber(tenantId, 'RESERVATION');
                }
                return num;
            }).then(num => {
                if (num) setReservedDocNumber(num);
                fetchingRef.current = false;
            }).catch(() => {
                fetchingRef.current = false;
            });
        }
    }, [tenantId, reservedDocNumber, location.state]); // Depend on reservedDocNumber to refetch if it's cleared (e.g. after save)

    // Load for Edit Logic
    useEffect(() => {
        if (location.state?.editReservation) {
            const r = location.state.editReservation;
            console.log('Editing Reservation:', r);
            setEditingReservationId(r.id);

            // Time parsing
            const dateStr = new Date(r.startTime).toISOString().split('T')[0];

            setFormData(prev => ({
                ...prev,
                transporterName: r.transporterName || '',
                transporterId: r.transporterId || null,
                transporterShortName: r.transporterShortName || '',
                plateNumber: r.plateNumber || '',
                transportType: r.transportType || '',
                vehicleType: r.metrics?.vehicleType || r.vehicleType || r.vehicleTypeId || '',
                vehicleTypeName: r.vehicleTypeName || '',
                volume: r.metrics?.volume || '',
                weight: r.metrics?.weight || '',
                date: dateStr,
                notes: r.notes || '',
                driverName: r.driverName || '',
                driverPhone: r.driverPhone || '',
                shareNotes: false
            }));

            // Set Document Number
            if (r.docNo || r.documentNumber) setReservedDocNumber(r.docNo || r.documentNumber);

            // If dock assigned, switch to manual and set selection
            if (r.dockId && docks.length > 0) {
                const dock = docks.find(d => d.id === r.dockId);
                if (dock) {
                    setManualSelection({
                        factoryId: dock.factoryId || '',
                        doorGroupId: dock.doorGroupId || '',
                        dockId: r.dockId,
                        selectedSlotTime: new Date(r.startTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                    });
                    setActiveTab('manual');
                }
            }
        }
    }, [location.state, docks]); // Keep docks in dep array for the inner check

    // Update Daily Data on Date Change
    useEffect(() => {
        if (tenantId && formData.date) {
            loadDailyData();
        }
    }, [tenantId, formData.date]);

    // Recalculate Logic
    useEffect(() => {
        // Find selected vehicle type limitations first
        const selectedType = vehicleTypes.find(t => t.id === formData.vehicleType);

        // Find Loading Rule (Strict Validation)
        const loadingRule = selectedType
            ? loadingRules.find(r => r.vehicleTypeCode === selectedType.code)
            : null;

        // Validation: If type is selected but no rule exists
        if (selectedType && !loadingRule) {
            // Only log/toast if we have a type selected. 
            // To prevent infinite toast loops, we might check if we already warned or just show visual indication?
            // For strict requirements: "Hata versin"
            // Using console.error for now to avoid UI blocking, or we can set a visual error state.
            console.warn(`No loading rule found for vehicle type code: ${selectedType.code}`);
            // If we must show error to user immediately:
            // toastError(`'${selectedType.name}' için yükleme kuralı bulunamadı!`); 
            // Note: Putting toast inside useEffect may spam if dependencies change often.
        }

        // Recalculate Logic
        // calculateLoadTime now accepts STRICT loadingRule
        const time = calculateLoadTime(formData.volume, formData.weight, loadingRule);

        const maxVol = selectedType?.volumeRange?.max || 90;
        const maxWeight = selectedType?.weightRange?.max || 24;

        const details = calculateFullnessDetails(formData.volume, formData.weight, maxVol, maxWeight);

        // Calculate Reservation Duration (Slot Aligned)
        const configStep = systemConfig?.slotDuration ? parseInt(systemConfig.slotDuration) : null;
        const slotStep = configStep || 30;
        const resDuration = Math.ceil(time / slotStep) * slotStep;

        setMetrics({
            loadTime: time,
            reservationDuration: resDuration,
            fullness: details.total,
            volumePct: details.volumePct,
            weightPct: details.weightPct,
            missingConfig: !configStep // Flag to show error in UI only
        });

        // Update suggestions if we have data
        if (docks.length > 0) {
            const suggs = getSuggestions(
                reservations,
                docks,
                resDuration,
                new Date(formData.date),
                slotStep,
                dockTypes,
                selectedType?.code
            );
            setSuggestions(suggs);

            // Try to preserve selection if it still exists in the new suggestions
            // This prevents "disappearing into waiting list" if user edits form after selecting a slot
            if (selectedSuggestion) {
                const stillExists = suggs.find(s =>
                    s.dockId === selectedSuggestion.dockId &&
                    s.startTime.getTime() === selectedSuggestion.startTime.getTime()
                );
                if (stillExists) {
                    setSelectedSuggestion(stillExists);
                } else {
                    setSelectedSuggestion(null);
                }
            } else {
                setSelectedSuggestion(null);
            }
        }
    }, [formData.volume, formData.weight, formData.vehicleType, formData.date, docks, reservations, vehicleTypes, systemConfig, loadingRules]);

    const loadInitialData = async () => {
        try {
            // Fetch Meta Data
            const [tData, vData, mData, fData, dgData, rulesData, dtData] = await Promise.all([
                getAllTransporters(tenantId, false),
                getVehicleTypes(tenantId),
                getTransportModes(tenantId),
                getAllFactories(tenantId),
                getAllDoorGroups(tenantId),
                getLoadingRules(tenantId),
                getDockTypes(tenantId)
            ]);

            // Scope Filtering
            let filteredFactories = fData;
            let filteredGroups = dgData;

            if (!scope.isFullAccess) {
                if (scope.factoryIds.length > 0) {
                    filteredFactories = fData.filter(f => scope.factoryIds.includes(f.id));
                }
                if (scope.doorGroupIds.length > 0) {
                    filteredGroups = dgData.filter(g => scope.doorGroupIds.includes(g.id));
                }
            }

            setTransporters(tData);
            setVehicleTypes(vData);
            setTransportModes(mData);
            setFactories(filteredFactories);
            setDoorGroups(filteredGroups);
            setLoadingRules(rulesData);
            setDockTypes(dtData);

            // Pre-select first factory if scoped
            if (filteredFactories.length > 0 && !manualSelection.factoryId) {
                setManualSelection(prev => ({ ...prev, factoryId: filteredFactories[0].id }));
            }

            // Load System Config (General Settings)
            const settings = await getGeneralSettings(tenantId);
            setSystemConfig(settings);

            await loadDailyData();
        } catch (err) {
            console.error(err);
            toastError('Veriler yüklenemedi');
        }
    };

    const loadDailyData = async () => {
        try {
            const [resData, dockData] = await Promise.all([
                getDailyReservations(tenantId, formData.date),
                getDocks(tenantId)
            ]);

            // Scope Filtering for Docks
            let filteredDocks = dockData;
            if (!scope.isFullAccess) {
                filteredDocks = dockData.filter(d => {
                    if (scope.dockIds.length > 0 && !scope.dockIds.includes(d.id)) return false;
                    if (scope.doorGroupIds.length > 0 && !scope.doorGroupIds.includes(d.doorGroupId)) return false;
                    if (scope.factoryIds.length > 0 && !scope.factoryIds.includes(d.factoryId)) return false;
                    return true;
                });
            }

            setReservations(resData);
            setDocks(filteredDocks);

            // Consume Sequence Number Immediately (Per Rule)
            // Logic moved to separate useEffect to prevent multiple triggers on date change

        } catch (err) {
            console.error(err);
        }
    };

    const handleTransporterSelect = (item) => {
        if (!item) return; // [FIX] Prevent null pointer error
        setFormData({
            ...formData,
            transporterName: item.companyName,
            transporterId: item.id,
            transporterShortName: item.shortName
        });
    };

    const handleApplySuggestion = (sugg) => {
        if (selectedSuggestion?.dockId === sugg.dockId && selectedSuggestion?.startTime.getTime() === sugg.startTime.getTime()) {
            setSelectedSuggestion(null); // Deselect
        } else {
            setSelectedSuggestion(sugg); // Select
        }
    };



    const handleManualSlotSelect = (timeSlot) => {
        setManualSelection(prev => ({ ...prev, selectedSlotTime: timeSlot }));
    };

    const handleManualModalSelect = (selectionResult) => {
        setManualSelection(selectionResult); // Update state with the result from modal
        setIsManualModalOpen(false);
    };

    const handleSave = async (suggestion = null) => {
        if (!formData.transporterId) {
            toastError('Lütfen taşıyıcı seçiniz.');
            return;
        }

        setLoading(true);
        try {
            // Determine time slot
            let startTime = null;
            let endTime = null;
            let dockId = null;
            let status = 'pending'; // Default status

            // LOGIC: If a suggestion is selected, use it.
            if (activeTab === 'smart' && selectedSuggestion) {
                if (metrics.loadTime <= 0) {
                    toastError('Yükleme süresi hesaplanamadı. Lütfen hacim/ağırlık giriniz.');
                    setLoading(false);
                    return;
                }
                startTime = selectedSuggestion.startTime;
                endTime = selectedSuggestion.endTime;
                dockId = selectedSuggestion.dockId;
            }
            // LOGIC: If manual tab is active
            else if (activeTab === 'manual') {
                // Check if a slot is actually selected
                if (manualSelection.dockId && manualSelection.selectedSlotTime) {
                    if (metrics.loadTime <= 0) {
                        toastError('Yükleme süresi hesaplanamadı. Lütfen hacim/ağırlık giriniz.');
                        setLoading(false);
                        return;
                    }
                    const [h, m] = manualSelection.selectedSlotTime.split(':').map(Number);
                    startTime = new Date(formData.date);
                    startTime.setHours(h, m, 0, 0);

                    endTime = new Date(startTime);
                    endTime.setMinutes(endTime.getMinutes() + metrics.reservationDuration);

                    dockId = manualSelection.dockId;
                } else {
                    // No slot selected -> "Allow without slot"
                    status = 'waiting_reservation';
                }
            } else {
                // Smart tab active but no suggestion passed -> User clicked "Save" globally?
                // In smart mode, if user clicks Save without picking a suggestion, 
                // we treat it as "Waiting for reservation" (just registering the vehicle)
                status = 'waiting_reservation';
            }

            // Extract factory and door group IDs for denormalization (easier filtering)
            let factoryId = null;
            let doorGroupId = null;

            if (startTime && dockId) {
                // Find IDs from suggestion or manual selection
                if (activeTab === 'smart' && selectedSuggestion) {
                    factoryId = selectedSuggestion.factoryId || null;
                    doorGroupId = selectedSuggestion.doorGroupId || null;
                } else if (activeTab === 'manual') {
                    factoryId = manualSelection.factoryId || null;
                    doorGroupId = manualSelection.doorGroupId || null;
                }
            }

            // Find vehicle type CODE for compatibility checks
            const selectedType = vehicleTypes.find(t => t.id === formData.vehicleType);

            const payload = {
                dockId,
                factoryId,
                doorGroupId,
                startTime,
                endTime,
                plateNumber: formData.plateNumber || 'PLAKA BELİRSİZ',
                transportType: formData.transportType,
                transporterName: formData.transporterName,
                transporterId: formData.transporterId,
                transporterShortName: formData.transporterShortName,
                tenantId: user.tenantId,
                status: status,
                notes: formData.notes,
                metrics: {
                    volume: formData.volume,
                    weight: formData.weight,
                    vehicleType: formData.vehicleType,
                    vehicleTypeCode: selectedType?.code || '',
                    estimatedDuration: metrics.reservationDuration,
                    fullness: metrics.fullness
                }
            };

            // Use Reserved Document Number
            const docNum = reservedDocNumber;

            if (docNum) {
                payload.documentNumber = docNum;
            } else {
                console.warn('Rezervasyon numarası (reservedDocNumber) bulunamadı.');
            }

            if (docNum) {
                payload.documentNumber = docNum;
            }

            // Add Driver Info
            payload.driverName = formData.driverName;
            payload.driverPhone = formData.driverPhone;

            if (editingReservationId) {
                // UPDATE
                await updateReservation(editingReservationId, payload, userData, user.tenantId);
                success(`Güncelleme Başarılı! Belge No: ${docNum || '-'}`);
                setEditingReservationId(null); // Clear edit mode
            } else {
                // CREATE
                await createReservation(payload, userData);
                success(status === 'waiting_reservation' ? `Kayıt Başarılı! Belge No: ${docNum || '-'}` : `Kayıt Başarılı! Belge No: ${docNum || '-'}`);
            }

            // Clear draft on success
            localStorage.removeItem('vehiclePlanningDraft');

            // Reset form and fetch NEXT number for next operation
            setFormData({
                ...initialFormData,
                date: formData.date // Keep the current date
            });
            setSelectedSuggestion(null);

            // Fetch NEW number immediately for the next action
            getNextSequenceNumber(user.tenantId, 'RESERVATION').then(num => setReservedDocNumber(num));

            loadDailyData(); // Refresh to update suggestions
        } catch (err) {
            toastError('Hata: ' + err.message);

        } finally {
            setLoading(false);
        }
    };

    // Helper to get icon for type
    const getIconForType = (code) => {
        const lower = code?.toLowerCase() || '';
        if (lower.includes('tir') || lower.includes('truck')) return '🚛';
        if (lower.includes('van')) return '🚐';
        if (lower.includes('kamyon')) return '🚚';
        return '🚙';
    };

    return (
        <div className="container-fluid mt-lg" style={{ paddingBottom: '80px' }}>


            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(350px, 1.2fr) minmax(300px, 1fr)', gap: 'var(--spacing-lg)', alignItems: 'start' }}>

                {/* LEFT PANEL: OPERATION FORM */}
                <div className="card-premium glow-theme p-lg">
                    <div className="flex justify-between items-center mb-lg pb-md" style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <div className="flex items-center gap-md">
                            <span style={{ fontSize: '1.5rem' }}>📋</span>
                            <h3 className="text-xl font-bold text-primary m-0">Operasyon Detayları</h3>
                        </div>
                        <div className="flex gap-md">
                            <div className="form-group mb-0">
                                <input
                                    type="date"
                                    className="input"
                                    value={formData.date}
                                    min={new Date().toISOString().split('T')[0]} // Restrict past dates
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    style={{ height: '38px' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Document Number & Transporter ROW */}
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-md)' }}>
                        <div className="form-group">
                            <label className="form-label">Belge No</label>
                            <input
                                type="text"
                                className="input bg-white/5 text-gray-400 cursor-not-allowed font-mono text-center tracking-wider"
                                value={reservedDocNumber || 'Yükleniyor...'}
                                readOnly
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Nakliye Firması</label>
                            <Combobox
                                items={transporters.filter(t =>
                                    !formData.transportType ||
                                    !t.supportedTransportModes ||
                                    t.supportedTransportModes.length === 0 ||
                                    t.supportedTransportModes.includes(formData.transportType)
                                )}
                                labelKey="companyName"
                                valueKey="id"
                                searchKeys={['companyName', 'taxNumber', 'phone']}
                                value={formData.transporterName}
                                onChange={handleTransporterSelect}
                                onInputChange={(val) => setFormData({ ...formData, transporterName: val, transporterId: null })}
                                placeholder="Firma ara..."
                            />
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '4px', textAlign: 'right', fontStyle: 'italic' }}>
                                Sistemde kayıtlı toplam {transporters.length} nakliyeci bulundu.
                            </div>
                            {formData.transporterId && (
                                <div className="mt-sm flex items-center gap-xs text-sm" style={{ color: 'var(--status-completed)', fontWeight: 600 }}>
                                    <span className="badge badge-completed" style={{ fontSize: '0.75rem' }}>✓ Onaylı</span>
                                    <span>{formData.transporterName}</span>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* Row 2: Transport Mode & Vehicle Type (Side-by-side) */}
                    <div className="grid grid-cols-2 gap-md mb-md">
                        <div className="form-group">
                            <label className="form-label">Nakliye Türü</label>
                            <select
                                className="input"
                                value={formData.transportType}
                                onChange={e => setFormData({ ...formData, transportType: e.target.value })}
                                style={{ height: '52px', fontSize: '1rem' }}
                            >
                                <option value="">Tür Seçiniz</option>
                                {transportModes.map(mode => (
                                    <option key={mode.id} value={mode.code}>
                                        {mode.code} - {mode.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Araç Tipi</label>
                            <select
                                className="input"
                                value={formData.vehicleType}
                                onChange={e => {
                                    const selected = vehicleTypes.find(t => t.id === e.target.value);
                                    setFormData({
                                        ...formData,
                                        vehicleType: e.target.value,
                                        vehicleTypeName: selected ? selected.name : ''
                                    });
                                }}
                                style={{ height: '52px', fontSize: '1rem' }}
                            >
                                <option value="">Araç Tipi Seçiniz</option>
                                {vehicleTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {getIconForType(type.code)} {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Driver & Plate Info */}
                    <div className="grid grid-cols-3 gap-md mb-md">
                        <div className="form-group">
                            <label className="form-label">Plaka</label>
                            <input
                                type="text"
                                className="input uppercase"
                                value={formData.plateNumber}
                                onChange={e => setFormData({ ...formData, plateNumber: e.target.value.toUpperCase() })}
                                placeholder="34 ABC 123"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Sürücü Adı</label>
                            <input
                                type="text"
                                className="input"
                                value={formData.driverName}
                                onChange={e => setFormData({ ...formData, driverName: e.target.value })}
                                placeholder="Ad Soyad"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Sürücü Telefon</label>
                            <input
                                type="tel"
                                className="input"
                                value={formData.driverPhone}
                                onChange={e => setFormData({ ...formData, driverPhone: e.target.value })}
                                placeholder="0555 555 55 55"
                            />
                        </div>
                    </div>


                    {/* Row 4: Volume & Weight with Validation */}
                    {(() => {
                        const selectedType = vehicleTypes.find(t => t.id === formData.vehicleType);

                        // Limits
                        const minVol = selectedType?.volumeRange?.min || 0;
                        const maxVol = selectedType?.volumeRange?.max || 999;
                        const minWeight = selectedType?.weightRange?.min || 0;
                        const maxWeight = selectedType?.weightRange?.max || 999;

                        // Validation Check Helper
                        const getStatusStyle = (val, min, max) => {
                            if (!val) return {}; // Default style
                            const num = parseFloat(val);
                            if (num >= min && num <= max) {
                                return {
                                    backgroundColor: 'rgba(16, 185, 129, 0.2)', // Green tint
                                    borderColor: 'var(--status-completed)',
                                    color: '#000000', // Siyah (black) for better readability on light theme
                                    fontWeight: 'bold'
                                };
                            }
                            return {
                                backgroundColor: 'rgba(239, 68, 68, 0.2)', // Red tint
                                borderColor: 'var(--status-cancelled)',
                                color: '#000000', // Siyah (black) for better readability on light theme
                                fontWeight: 'bold'
                            };
                        };

                        return (
                            <div className="grid grid-cols-2 gap-md mb-lg">
                                {/* Volume Input */}
                                <div className="form-group">
                                    <div className="flex justify-between items-baseline mb-1">
                                        <label className="form-label mb-0">Hacim (m³)</label>
                                        {selectedType && (
                                            <span className="text-[10px] opacity-70 font-mono">
                                                Min:{minVol} - Max:{maxVol}
                                            </span>
                                        )}
                                    </div>
                                    <input
                                        type="number"
                                        className="input font-bold"
                                        value={formData.volume}
                                        onChange={e => setFormData({ ...formData, volume: e.target.value })}
                                        placeholder="0"
                                        style={getStatusStyle(formData.volume, minVol, maxVol)}
                                    />
                                </div>

                                {/* Weight Input */}
                                <div className="form-group">
                                    <div className="flex justify-between items-baseline mb-1">
                                        <label className="form-label mb-0">Ağırlık ({systemConfig?.weightUnit || 'Kg'})</label>
                                        {selectedType && (
                                            <span className="text-[10px] opacity-70 font-mono">
                                                Min:{minWeight} - Max:{maxWeight}
                                            </span>
                                        )}
                                    </div>
                                    <input
                                        type="number"
                                        className="input font-bold"
                                        value={formData.weight}
                                        onChange={e => setFormData({ ...formData, weight: e.target.value })}
                                        placeholder="0"
                                        style={getStatusStyle(formData.weight, minWeight, maxWeight)}
                                    />
                                </div>
                            </div>
                        );
                    })()}

                    <div className="form-group">
                        <label className="form-label">Notlar</label>
                        <textarea
                            className="textarea"
                            value={formData.notes}
                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                            placeholder="Operasyon notları..."
                            rows={3}
                        ></textarea>
                    </div>
                </div>

                {/* RIGHT PANEL: INTELLIGENCE & SUMMARY */}
                <div className="flex flex-col gap-lg">

                    {/* Top Stats Cards */}
                    <div className="grid grid-cols-2 gap-md">
                        <div className="card-premium glow-blue p-md text-center flex flex-col items-center justify-center py-4">
                            <div className="text-secondary text-xs uppercase tracking-wider mb-2">Rezervasyon Süresi</div>
                            {metrics.missingConfig ? (
                                <>
                                    <div className="text-xl font-bold mb-1 text-red-500">AYAR EKSİK</div>
                                    <div className="text-[10px] text-red-400">Slot süresi tanımlanmamış!</div>
                                </>
                            ) : (
                                <>
                                    <div className="text-xl font-bold text-white mb-1">{metrics.reservationDuration} dk</div>
                                    <div className="text-[10px] text-gray-400 opacity-60">
                                        (Slot Aralığı: {systemConfig?.slotDuration || 30} dk)
                                    </div>
                                </>
                            )}

                            <div className="w-full h-px bg-border my-2"></div>

                            <div className="text-secondary text-[10px] uppercase tracking-wider">Hesaplanan Süre</div>
                            <div className="text-sm font-bold text-primary">{metrics.loadTime} dk</div>
                        </div>
                        <div className="card-premium glow-blue p-md w-full flex flex-col justify-center py-4" style={{ gap: '16px' }}>
                            <SegmentedProgressBar
                                value={metrics.volumePct}
                                label="Hacim Doluluğu"
                            />
                            <SegmentedProgressBar
                                value={metrics.weightPct}
                                label="Ağırlık Doluluğu"
                            />
                        </div>
                    </div>



                    {/* Tab Navigation */}
                    <div className="flex mb-4 gap-3">
                        <button
                            className={`btn-tab-glass ${activeTab === 'smart' ? 'btn-tab-glass-active' : ''}`}
                            onClick={() => setActiveTab('smart')}
                        >
                            <span className="text-xl">🤖</span> Akıllı Öneriler
                        </button>
                        <button
                            className={`btn-tab-glass ${activeTab === 'manual' ? 'btn-tab-glass-active' : ''}`}
                            onClick={() => setActiveTab('manual')}
                        >
                            <span className="text-xl">👆</span> Manuel Seçim
                        </button>
                    </div>

                    {/* Content Area */}
                    <div className="card-premium glow-theme" style={{ padding: 0, overflow: 'hidden', minHeight: '400px' }}>

                        {activeTab === 'smart' ? (
                            <>
                                <div className="p-md flex justify-between items-center" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                                    <h3 className="text-base font-bold m-0 flex items-center gap-sm">
                                        Önerilen Slotlar
                                    </h3>
                                    {suggestions.length > 0 && <span className="badge badge-primary">{suggestions.length} Slot</span>}
                                </div>

                                <div className="p-md" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {suggestions.length === 0 ? (
                                        <div className="text-center py-xl opacity-50">
                                            {metrics.loadTime > 0 ? (
                                                <>
                                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>⚠️</div>
                                                    <p>Bu kriterlere uygun boş slot bulunamadı.</p>
                                                    <span className="text-xs">Tarihi değiştirmeyi veya manuel seçimi deneyin.</span>
                                                </>
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🔍</div>
                                                    <p>Veri girişi bekleniyor...</p>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-sm">
                                            {suggestions.map((sugg, idx) => {
                                                const isSelected = selectedSuggestion &&
                                                    selectedSuggestion.dockId === sugg.dockId &&
                                                    selectedSuggestion.startTime.getTime() === sugg.startTime.getTime();

                                                return (
                                                    <div
                                                        key={idx}
                                                        onClick={() => handleApplySuggestion(sugg)}
                                                        className={`nav-item ${isSelected ? 'selected-suggestion' : ''}`}
                                                        style={{
                                                            padding: '12px',
                                                            border: isSelected ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                                                            borderRadius: '12px',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            background: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-md">
                                                            <div className="badge badge-alternative" style={{ fontSize: '1rem', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {sugg.dockName.replace(/\D/g, '') || sugg.dockName.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-primary">{sugg.dockName}</div>
                                                                <div className="text-xs text-secondary">
                                                                    {sugg.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {sugg.endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div className="text-xs font-bold text-success">%{sugg.score} Uygun</div>
                                                            {isSelected ? (
                                                                <div className="text-xs font-bold text-primary flex items-center justify-end gap-1">
                                                                    <span>✓</span> Seçildi
                                                                </div>
                                                            ) : (
                                                                <div className="text-xs text-secondary opacity-70">Seçmek için tıkla</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="p-lg flex flex-col items-center justify-center h-full text-center">
                                <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-md)' }}>👆</div>
                                <h3 className="text-lg font-bold mb-sm">Manuel Rampa Seçimi</h3>
                                <p className="text-secondary mb-lg">Fabrika, rampa grubu ve rampa seçerek müsaitlik durumuna göre işlem yapın.</p>

                                {manualSelection.selectedSlotTime && manualSelection.dockId ? (
                                    <div className="mb-lg p-md border rounded w-full bg-surface">
                                        <div className="text-sm text-secondary uppercase font-bold mb-xs">Seçilen Slot</div>
                                        <div className="text-xl font-bold text-primary">{manualSelection.selectedSlotTime}</div>
                                        <div className="text-sm font-medium">{docks.find(d => d.id === manualSelection.dockId)?.name || 'Rampa'}</div>
                                    </div>
                                ) : (
                                    <div className="mb-lg text-secondary opacity-50 italic">Henüz bir slot seçilmedi.</div>
                                )}

                                <Button variant="secondary" onClick={() => setIsManualModalOpen(true)} className="w-full">
                                    {manualSelection.selectedSlotTime ? 'Seçimi Düzenle' : 'Rampa ve Saat Seç'}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Action Button */}
                    <div className="mt-auto">
                        <Button
                            variant="primary"
                            className="w-full btn-lg"
                            onClick={(e) => {
                                e.preventDefault();
                                handleSave(null);
                            }}
                            disabled={loading || (editingReservationId ? !can(PERMISSIONS.PLANNING, PERMISSIONS.EDIT) : !can(PERMISSIONS.PLANNING, PERMISSIONS.CREATE))}
                            style={{
                                boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)', // Premium glow from theme
                                fontSize: '1.1rem',
                                opacity: (editingReservationId ? !can(PERMISSIONS.PLANNING, PERMISSIONS.EDIT) : !can(PERMISSIONS.PLANNING, PERMISSIONS.CREATE)) ? 0.5 : 1
                            }}
                        >
                            {loading ? 'İşleniyor...' : 'Planlamayı Kaydet'}
                        </Button>
                        <p className="text-xs text-secondary text-center mt-sm">
                            *Seçilen slot için otomatik bildirim gönderilecektir.
                        </p>
                    </div>

                </div>
            </div>

            {/* Manual Selection Modal */}
            <ManualSlotSelectionModal
                isOpen={isManualModalOpen}
                onClose={() => setIsManualModalOpen(false)}
                onSelect={handleManualModalSelect}
                factories={factories}
                doorGroups={doorGroups}
                docks={docks}
                reservations={reservations}
                initialSelection={manualSelection}
                selectedDate={formData.date}
                onDateChange={(newDate) => setFormData(prev => ({ ...prev, date: newDate }))}
                duration={metrics.loadTime}
                slotDuration={systemConfig?.slotDuration || 30}
                dockTypes={dockTypes}
                vehicleTypeCode={vehicleTypes.find(t => t.id === formData.vehicleType)?.code}
            />
        </div>
    );


};

export default VehiclePlanning;
